'use client'

import { useState, useRef, useEffect } from 'react'
// Using regular img tag instead of Next.js Image component
import { Property } from '@/types'
import PropertyImageManager from './PropertyImageManager'
import { useToast } from '@/hooks/useToast'

interface PropertyFormProps {
  property?: Property | null
  onSuccess?: () => void
  onCancel?: () => void
}

export default function PropertyForm({ property, onSuccess, onCancel }: PropertyFormProps) {
  const [loading, setLoading] = useState(false)
  const toast = useToast()
  // Extract image URLs from property - handle both string[] and object[] formats
  const getImageUrls = (propertyImages: any): string[] => {
    if (!propertyImages || propertyImages.length === 0) return []
    // If images are strings, return as is
    if (typeof propertyImages[0] === 'string') {
      return propertyImages.filter((url: string) => url && url.trim() !== '')
    }
    // If images are objects, extract URLs
    return propertyImages
      .map((img: any) => (typeof img === 'object' && img !== null ? img.url : img))
      .filter((url: string) => url && url.trim() !== '')
  }

  const [images, setImages] = useState<string[]>(() => getImageUrls(property?.images))
  
  // Handler to update images when PropertyImageManager uploads new images
  const handleImagesChange = (newImageUrls: string[]) => {
    console.log('🖼️ Images updated in PropertyForm:', newImageUrls)
    setImages(newImageUrls)
  }
  
  // Update images when property changes (for editing)
  useEffect(() => {
    if (property?.images) {
      const imageUrls = getImageUrls(property.images)
      setImages(imageUrls)
    }
  }, [property?.images])
  const [videos, setVideos] = useState<string[]>(property?.videos || [])
  const [houseDocuments, setHouseDocuments] = useState<string[]>(property?.house_documents || [])
  const [uploadingDocs, setUploadingDocs] = useState(false)
  const [uploadingVideos, setUploadingVideos] = useState(false)
  const [userProfile, setUserProfile] = useState<Record<string, unknown> | null>(null)
  const [checkingVerification, setCheckingVerification] = useState(true)
  // const fileInputRef = useRef<HTMLInputElement>(null)
  const videoFileInputRef = useRef<HTMLInputElement>(null)
  const docsFileInputRef = useRef<HTMLInputElement>(null)

  // Property type and amenities options (Nigeria-focused)
  // Map frontend values to backend enum values
  const propertyTypes = [
    { value: 'self_contain', label: 'Self Contain (Studio)', backendValue: 'studio' },
    { value: 'mini_flat', label: 'Mini Flat (1 Bedroom)', backendValue: 'apartment' },
    { value: 'one_bedroom', label: '1 Bedroom Flat', backendValue: 'apartment' },
    { value: 'two_bedroom', label: '2 Bedroom Flat', backendValue: 'apartment' },
    { value: 'three_bedroom', label: '3 Bedroom Flat', backendValue: 'apartment' },
    { value: 'four_bedroom', label: '4 Bedroom Flat', backendValue: 'apartment' },
    { value: 'bungalow', label: 'Bungalow', backendValue: 'house' },
    { value: 'detached_duplex', label: 'Detached Duplex', backendValue: 'house' },
    { value: 'semi_detached_duplex', label: 'Semi-Detached Duplex', backendValue: 'house' },
    { value: 'terraced_duplex', label: 'Terraced Duplex', backendValue: 'townhouse' },
    { value: 'mansion', label: 'Mansion', backendValue: 'house' },
    { value: 'penthouse', label: 'Penthouse', backendValue: 'apartment' },
    { value: 'shop', label: 'Shop/Office Space', backendValue: 'other' },
    { value: 'warehouse', label: 'Warehouse', backendValue: 'other' }
  ]
  
  // Helper to get backend property type value
  const getBackendPropertyType = (frontendValue: string) => {
    const type = propertyTypes.find(t => t.value === frontendValue)
    return type?.backendValue || 'other'
  }

  const amenitiesOptions = [
    { value: 'parking', label: 'Parking' },
    { value: 'security', label: 'Security' },
    { value: 'gated_estate', label: 'Gated Estate' },
    { value: 'cctv', label: 'CCTV' },
    { value: '24_7_electricity', label: '24/7 Electricity' },
    { value: 'inverter_backup', label: 'Inverter/Backup Power' },
    { value: 'prepaid_meter', label: 'Prepaid Meter' },
    { value: 'solar_power', label: 'Solar Power' },
    { value: 'generator_house', label: 'Generator House' },
    { value: 'borehole', label: 'Borehole' },
    { value: 'water_supply', label: 'Water Supply' },
    { value: 'water_heater', label: 'Water Heater' },
    { value: 'fitted_kitchen', label: 'Fitted Kitchen' },
    { value: 'cooker', label: 'Cooker' },
    { value: 'microwave', label: 'Microwave' },
    { value: 'refrigerator', label: 'Refrigerator' },
    { value: 'wardrobes', label: 'Wardrobes' },
    { value: 'pop_ceiling', label: 'POP Ceiling' },
    { value: 'air_conditioning', label: 'Air Conditioning' },
    { value: 'balcony', label: 'Balcony' },
    { value: 'elevator', label: 'Elevator' },
    { value: 'swimming_pool', label: 'Swimming Pool' },
    { value: 'gym', label: 'Gym' },
    { value: 'playground', label: 'Playground' },
    { value: 'internet', label: 'Internet' },
    { value: 'interlocked_compound', label: 'Interlocked Compound' },
    { value: 'paved_road_access', label: 'Paved Road Access' },
    { value: 'ensuite_rooms', label: 'All Rooms Ensuite' },
    { value: 'visitors_toilet', label: 'Visitor\'s Toilet' },
    { value: 'washing_machine', label: 'Washing Machine' },
    { value: 'green_area', label: 'Green Area' },
    { value: 'study', label: 'Study' },
    { value: 'boys_quarters', label: 'Boys\' Quarters (BQ)' },
    { value: 'jacuzzi', label: 'Jacuzzi' }
  ]

  // Form state
  const [formData, setFormData] = useState(() => {
    // Handle address - could be string (old format) or object (new format)
    let addressObj: { street?: string; city?: string; state?: string; zipCode?: string; country?: string } = {}
    let addressString = ''
    
    if (property?.address) {
      if (typeof property.address === 'string') {
        addressString = property.address
      } else {
        addressObj = property.address as { street?: string; city?: string; state?: string; zipCode?: string; country?: string }
      }
    }
    
    return {
      title: property?.title || '',
      description: property?.description || '',
      price: property?.price ? property.price.toString() : '',
      duration: property?.duration ? Math.min(property.duration, 12) : 12,
      // Address fields (matching backend structure)
      street: addressObj?.street || addressString || '',
      city: addressObj?.city || property?.city || '',
      state: addressObj?.state || property?.state || '',
      zipCode: addressObj?.zipCode || property?.postal_code || '',
      country: addressObj?.country || 'US',
      // Property details
      property_type: property?.property_type || 'self_contain',
      bedrooms: property?.bedrooms ? property.bedrooms.toString() : '1',
      bathrooms: property?.bathrooms ? property.bathrooms.toString() : '1',
      squareFeet: property?.area ? property.area.toString() : '',
      rentalType: property?.rentalType || 'long-term', // Default to long-term
      amenities: property?.amenities || [],
      // Shortlet availability dates
      availableDates: property?.availableDates || [] // Array of date ranges: [{start: 'YYYY-MM-DD', end: 'YYYY-MM-DD'}]
    }
  })
  
  // State for managing date ranges for shortlets
  const [dateRanges, setDateRanges] = useState<Array<{start: string, end: string}>>(() => {
    if (property?.availableDates && property.availableDates.length > 0) {
      // Convert date objects/strings to YYYY-MM-DD format
      return property.availableDates.map(range => ({
        start: typeof range.start === 'string' ? range.start.split('T')[0] : new Date(range.start).toISOString().split('T')[0],
        end: typeof range.end === 'string' ? range.end.split('T')[0] : new Date(range.end).toISOString().split('T')[0]
      }))
    }
    return []
  })
  
  // Initialize date ranges when rental type changes to short-term
  useEffect(() => {
    if (formData.rentalType === 'short-term' && dateRanges.length === 0) {
      setDateRanges([{ start: '', end: '' }])
    } else if (formData.rentalType === 'long-term') {
      setDateRanges([])
    }
  }, [formData.rentalType])

  // Check user verification status on component mount
  useEffect(() => {
    const checkUserVerification = async () => {
      try {
        const token = localStorage.getItem('authToken')
        
        if (!token) {
          toast.error('Authentication failed. Please sign in again.')
          return
        }

        const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5001/api'}/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })
        
        if (!response.ok) {
          toast.error('Authentication failed. Please sign in again.')
          return
        }

        const data = await response.json()
        // Backend returns { user: {...} }, so extract the user object
        const profile = data.user || data
        setUserProfile(profile)
        
        // Check if user is verified - check both is_verified and isVerified fields
        // Also check kyc.status for backward compatibility
        const isVerified = profile?.is_verified || profile?.isVerified || profile?.kyc?.status === 'verified'
        if (!isVerified) {
          toast.error('You must be verified by admin before you can list properties. Please contact support or wait for verification.')
        }
      } catch (error) {
        console.error('Error checking verification:', error)
        toast.error('Failed to check verification status.')
      } finally {
        setCheckingVerification(false)
      }
    }

    checkUserVerification()
  }, [])

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  // Handle amenities toggle
  const handleAmenityToggle = (amenity: string) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }))
  }

  // Handle image upload (removed for now)

  // Remove image (removed for now)

  // Handle video upload
  const handleVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    setUploadingVideos(true)
    const newVideos: string[] = []

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        
        // Validate file type (MP4, MOV, AVI, etc.)
        const allowedTypes = ['video/mp4', 'video/mov', 'video/avi', 'video/quicktime', 'video/webm']
        if (!allowedTypes.includes(file.type)) {
          toast.error('Only MP4, MOV, AVI, and WebM video files are allowed.')
          continue
        }

        // Validate file size (max 50MB for videos)
        if (file.size > 50 * 1024 * 1024) {
          toast.error('Videos must be less than 50MB each.')
          continue
        }

        // Validate video duration (max 5 minutes)
        const video = document.createElement('video')
        video.preload = 'metadata'
        
        await new Promise((resolve, reject) => {
          video.onloadedmetadata = () => {
            if (video.duration > 300) { // 5 minutes = 300 seconds
              reject(new Error('Videos must be less than 5 minutes long.'))
            } else {
              resolve(true)
            }
          }
          video.onerror = () => reject(new Error('Could not read video metadata'))
          video.src = URL.createObjectURL(file)
        })

        // Upload video to backend
        const token = localStorage.getItem('authToken')
        if (!token) {
          toast.error('Please log in to upload videos')
          return
        }

        const formData = new FormData()
        formData.append('file', file)
        formData.append('type', 'property-video')

        const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5001/api'}/upload/property-videos`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData
        })

        if (!response.ok) {
          toast.error('Failed to upload video. Please try again.')
          continue
        }

        const data = await response.json()
        newVideos.push(data.url)
      }

      if (newVideos.length > 0) {
        setVideos(prev => [...prev, ...newVideos])
        toast.success(`Successfully uploaded ${newVideos.length} video(s)!`)
      }
    } catch (error) {
      console.error('Error uploading videos:', error)
      if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('Failed to upload videos. Please try again.')
      }
    } finally {
      setUploadingVideos(false)
      // Reset file input
      if (videoFileInputRef.current) {
        videoFileInputRef.current.value = ''
      }
    }
  }

  // Handle video removal
  const removeVideo = (index: number) => {
    const newVideos = videos.filter((_, i) => i !== index)
    setVideos(newVideos)
  }

  // Handle house document upload
  const handleHouseDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Prevent any form submission
    event.preventDefault()
    event.stopPropagation()
    
    const files = event.target.files
    if (!files || files.length === 0) return

    setUploadingDocs(true)
    const newDocuments: string[] = []

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        
        // Validate file type (PDF, images, etc.)
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg']
        if (!allowedTypes.includes(file.type)) {
          toast.error('Only PDF and image files are allowed for house documents.')
          continue
        }

        // Validate file size (max 10MB for documents)
        if (file.size > 10 * 1024 * 1024) {
          toast.error('House documents must be less than 10MB each.')
          continue
        }

        const token = localStorage.getItem('authToken')
        if (!token) {
          toast.error('Please log in to upload documents')
          continue
        }

        const formData = new FormData()
        formData.append('file', file)

        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
        const response = await fetch(`${apiUrl}/upload/single`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            // Don't set Content-Type header - let browser set it with boundary for FormData
          },
          body: formData
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          toast.error(errorData.message || 'Failed to upload house document. Please try again.')
          continue
        }

        const data = await response.json()
        // The response structure is { file: { url: ... } } or { url: ... }
        const documentUrl = data.file?.url || data.url
        if (documentUrl) {
          newDocuments.push(documentUrl)
        } else {
          console.error('Invalid response structure:', data)
          toast.error('Invalid response from server. Please try again.')
          continue
        }
      }

      if (newDocuments.length > 0) {
        setHouseDocuments(prev => [...prev, ...newDocuments])
        toast.success(`Successfully uploaded ${newDocuments.length} house document(s)!`)
      } else {
        toast.error('No documents were uploaded. Please check file types and sizes.')
      }
    } catch (error) {
      console.error('Error uploading house documents:', error)
      if (error instanceof Error) {
        toast.error(`Upload error: ${error.message}`)
      } else {
        toast.error('Failed to upload house documents. Please try again.')
      }
    } finally {
      setUploadingDocs(false)
      // Reset file input
      if (docsFileInputRef.current) {
        docsFileInputRef.current.value = ''
      }
    }
  }

  // Handle house document removal
  const removeHouseDocument = (index: number) => {
    const newDocuments = houseDocuments.filter((_, i) => i !== index)
    setHouseDocuments(newDocuments)
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    // Prevent submission if documents are currently being uploaded
    if (uploadingDocs) {
      toast.error('Please wait for documents to finish uploading before saving.')
      return
    }
    
    // Check if user is verified - check multiple fields for compatibility
    const isVerified = userProfile?.is_verified || userProfile?.isVerified || (userProfile as any)?.kyc?.status === 'verified'
    if (!isVerified) {
      toast.error('You must be verified by admin before you can list properties. Please contact support or wait for verification.')
      return
    }

    setLoading(true)

    try {
      // Prevent editing of approved properties
      if (property && property.is_verified) {
        toast.error('Cannot edit approved properties. Contact support if changes are needed.')
        return
      }

      // Validate required house documents
      if (houseDocuments.length === 0) {
        toast.error('Please upload at least one house verification document before submitting.')
        setLoading(false)
        return
      }

      // Validate that we have at least one valid image (filter out empty URLs)
      const validImages = images.filter(url => url && typeof url === 'string' && url.trim() !== '')
      if (validImages.length === 0) {
        toast.error('Please upload at least one property image before submitting.')
        setLoading(false)
        return
      }

      // Validate required address fields
      if (!formData.street || !formData.city || !formData.state || !formData.zipCode) {
        toast.error('Please fill in all address fields (Street, City, State, Zip Code).')
        setLoading(false)
        return
      }

      // Validate bedrooms and bathrooms
      const bedrooms = parseInt(formData.bedrooms) || 0
      const bathrooms = parseFloat(formData.bathrooms) || 0
      if (bedrooms < 0 || bathrooms < 0) {
        toast.error('Bedrooms and bathrooms must be valid numbers.')
        setLoading(false)
        return
      }

      // Validate lease duration (maximum 12 months in Nigeria)
      if (formData.rentalType === 'long-term' && formData.duration > 12) {
        toast.error('Lease duration cannot exceed 12 months as per Nigeria rental regulations.')
        setLoading(false)
        return
      }

      // Transform data to match backend Property model structure
      const propertyData = {
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price.toString()) || 0,
        // Map property_type to backend enum
        propertyType: getBackendPropertyType(formData.property_type),
        // Address object (required by backend)
        address: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          country: formData.country || 'US'
        },
        // Bedrooms and bathrooms (required by backend)
        bedrooms: bedrooms,
        bathrooms: bathrooms,
        // Optional square feet
        squareFeet: formData.squareFeet ? parseFloat(formData.squareFeet) : undefined,
        // Rental type
        rentalType: formData.rentalType,
        // Lease terms for long-term rentals
        // Enforce maximum lease duration of 12 months (Nigeria rental law)
        leaseTerms: formData.rentalType === 'long-term' ? {
          minLease: Math.min(parseInt(formData.duration.toString()) || 12, 12),
          maxLease: 12 // Maximum allowed in Nigeria
        } : undefined,
        // Available dates for shortlets (convert to Date objects)
        availableDates: formData.rentalType === 'short-term' && dateRanges.length > 0 
          ? dateRanges.map(range => ({
              start: new Date(range.start),
              end: new Date(range.end)
            }))
          : undefined,
        // Images (backend expects array of objects with url, isPrimary, etc.)
        // Filter out empty URLs and only include valid image URLs
        // Use the current images state which contains the uploaded URLs from PropertyImageManager
        images: images && images.length > 0 
          ? images
              .filter((url: string) => url && typeof url === 'string' && url.trim() !== '') // Filter out empty or whitespace-only URLs
              .map((url: string, index: number) => ({ 
                url: url.trim(), 
                isPrimary: index === 0,
                caption: '',
                uploadedAt: new Date()
              }))
          : [],
        // Amenities and features
        amenities: formData.amenities || [],
        features: formData.amenities || [],
        // Status
        status: 'draft',
        isAvailable: true
      }
      
      // Add optional fields that backend may accept (Mongoose will ignore if not in schema)
      if (videos && videos.length > 0) {
        (propertyData as any).videos = videos
      }
      // Add house documents - backend expects houseDocuments array of objects
      if (houseDocuments && houseDocuments.length > 0) {
        (propertyData as any).houseDocuments = houseDocuments.map((url: string) => ({
          url: url,
          uploadedAt: new Date()
        }))
      }

      // Log the property data being sent (for debugging) - verify images are included
      console.log('📤 Sending property data to backend:', {
        title: propertyData.title,
        imagesCount: propertyData.images.length,
        imageUrls: propertyData.images.map((img: { url: string }) => img.url),
        rentalType: propertyData.rentalType
      })

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
      const token = localStorage.getItem('authToken')
      const url = property ? `${apiUrl}/properties/${property.id}` : `${apiUrl}/properties`
      const method = property ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(propertyData)
      })

      if (!response.ok) {
        const errorData = await response.json()
        toast.error(errorData.message || 'Failed to save property. Please try again.')
      } else {
        toast.success(property ? 'Property updated successfully!' : 'Property created successfully! It will be reviewed by admin before going live.')
        onSuccess?.()
      }
    } catch (error) {
      console.error('Form submission error:', error)
      toast.error('An error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Show loading state while checking verification
  if (checkingVerification) {
    return (
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking verification status...</p>
        </div>
      </div>
    )
  }

  // Show verification required message - check multiple fields for compatibility
  const isVerified = userProfile?.is_verified || userProfile?.isVerified || (userProfile as any)?.kyc?.status === 'verified'
  if (!isVerified) {
    return (
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6">
        <div className="text-center py-8">
          <div className="text-yellow-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Verification Required</h2>
          <p className="text-gray-600 mb-6">
            You must be verified by admin before you can list properties. This helps ensure the quality and authenticity of listings on our platform.
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-yellow-800 mb-2">What you need to do:</h3>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• Complete your profile with personal information</li>
              <li>• Upload required KYC documents</li>
              <li>• Wait for admin verification (usually 24-48 hours)</li>
              <li>• You&apos;ll receive an email when verified</li>
            </ul>
          </div>
          <button
            onClick={() => window.location.href = '/dashboard/landlord'}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    )
  }

  // Show property approved message - cannot edit
  if (property && property.is_verified) {
    return (
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6">
        <div className="text-center py-8">
          <div className="text-green-600 text-6xl mb-4">✅</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Property Already Approved</h2>
          <p className="text-gray-600 mb-6">
            This property has been verified and approved by our admin team. Approved properties cannot be edited to maintain listing integrity and prevent fraud.
          </p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-green-800 mb-2">Property Status:</h3>
            <ul className="text-sm text-green-700 space-y-1">
              <li>• Property Title: <strong>{property.title}</strong></li>
              <li>• Location: <strong>{property.location}</strong></li>
              <li>• Status: <strong className="text-green-600">✅ Verified & Live</strong></li>
              <li>• Listed on: <strong>{new Date(property.created_at).toLocaleDateString()}</strong></li>
            </ul>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-blue-800 mb-2">Need to make changes?</h3>
            <p className="text-sm text-blue-700">
              If you need to update your property details, please contact our support team. Major changes may require re-verification.
            </p>
          </div>
          <div className="flex space-x-4 justify-center">
            <button
              onClick={() => window.location.href = '/dashboard/landlord'}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
            >
              Back to Dashboard
            </button>
            <button
              onClick={onCancel}
              className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {property ? 'Edit Property' : 'Add New Property'}
        </h2>
        <p className="text-gray-600">
          {property ? 'Update your property details below.' : 'Fill in the details for your new rental property.'}
        </p>
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <span className="text-green-600 mr-2">✅</span>
            <span className="text-sm text-green-800">Your account is verified. Properties will be reviewed by admin before going live.</span>
          </div>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        onKeyDown={(e) => {
          // Prevent Enter from submitting the form so that property is only created/updated
          // when the user explicitly clicks "Create Property" or "Update Property", not when
          // they upload documents or press Enter in a field.
          if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
            e.preventDefault()
          }
        }}
        className="space-y-6"
      >
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Property Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
              placeholder="e.g., Modern 2BR Apartment in Downtown"
              required
            />
          </div>

          <div>
            <label htmlFor="property_type" className="block text-sm font-medium text-gray-700 mb-2">
              Property Type *
            </label>
            <select
              id="property_type"
              name="property_type"
              value={formData.property_type}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
              required
            >
              {propertyTypes.map(type => (
                <option key={type.value} value={type.value} className="text-gray-900">
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Address Fields */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Address *
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label htmlFor="street" className="block text-xs font-medium text-gray-600 mb-1">
                Street Address *
              </label>
              <input
                type="text"
                id="street"
                name="street"
                value={formData.street}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                placeholder="e.g., 123 Main Street"
                required
              />
            </div>
            <div>
              <label htmlFor="city" className="block text-xs font-medium text-gray-600 mb-1">
                City *
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                placeholder="e.g., Lagos"
                required
              />
            </div>
            <div>
              <label htmlFor="state" className="block text-xs font-medium text-gray-600 mb-1">
                State *
              </label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                placeholder="e.g., Lagos State"
                required
              />
            </div>
            <div>
              <label htmlFor="zipCode" className="block text-xs font-medium text-gray-600 mb-1">
                Zip/Postal Code *
              </label>
              <input
                type="text"
                id="zipCode"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                placeholder="e.g., 100001"
                required
              />
            </div>
            <div>
              <label htmlFor="country" className="block text-xs font-medium text-gray-600 mb-1">
                Country
              </label>
              <input
                type="text"
                id="country"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                placeholder="e.g., Nigeria"
              />
            </div>
          </div>
        </div>

        {/* Bedrooms, Bathrooms, Square Feet */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label htmlFor="bedrooms" className="block text-sm font-medium text-gray-700 mb-2">
              Bedrooms *
            </label>
            <input
              type="number"
              id="bedrooms"
              name="bedrooms"
              value={formData.bedrooms}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
              placeholder="2"
              min="0"
              required
            />
          </div>
          <div>
            <label htmlFor="bathrooms" className="block text-sm font-medium text-gray-700 mb-2">
              Bathrooms *
            </label>
            <input
              type="number"
              id="bathrooms"
              name="bathrooms"
              value={formData.bathrooms}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
              placeholder="2"
              min="0"
              step="0.5"
              required
            />
          </div>
          <div>
            <label htmlFor="squareFeet" className="block text-sm font-medium text-gray-700 mb-2">
              Square Feet (Optional)
            </label>
            <input
              type="number"
              id="squareFeet"
              name="squareFeet"
              value={formData.squareFeet}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
              placeholder="1200"
              min="0"
            />
          </div>
        </div>

        {/* Rental Type Selection */}
        <div>
          <label htmlFor="rentalType" className="block text-sm font-medium text-gray-700 mb-2">
            Rental Type *
          </label>
          <select
            id="rentalType"
            name="rentalType"
            value={formData.rentalType}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
            required
          >
            <option value="long-term">Long-term Rental</option>
            <option value="short-term">Short-term Rental (Shortlet)</option>
          </select>
          <p className="mt-1 text-sm text-gray-500">
            {formData.rentalType === 'short-term' 
              ? 'Shortlets are typically rented for days or weeks (e.g., vacation rentals, temporary stays)'
              : 'Long-term rentals are typically rented for months or years (e.g., residential leases)'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
              {formData.rentalType === 'short-term' ? 'Price per Day (₦)' : 'Monthly Rent (₦)'} *
            </label>
            <input
              type="number"
              id="price"
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
              placeholder={formData.rentalType === 'short-term' ? "100" : "1500"}
              min="0"
              step="0.01"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              {formData.rentalType === 'short-term' 
                ? 'Price charged per day for the shortlet' 
                : 'Monthly rental amount'}
            </p>
          </div>

          {formData.rentalType === 'long-term' && (
            <div>
              <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-2">
                Lease Duration (months) *
              </label>
              <select
                id="duration"
                name="duration"
                value={formData.duration}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                required
              >
                <option value={6}>6 months</option>
                <option value={12}>12 months (Maximum)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Note: Maximum lease duration in Nigeria is 12 months</p>
            </div>
          )}
        </div>

        {/* Date Range Picker for Shortlets */}
        {formData.rentalType === 'short-term' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Available Dates (Select date ranges when property is available) *
            </label>
            <div className="space-y-4">
              {dateRanges.map((range, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={range.start}
                      onChange={(e) => {
                        const newRanges = [...dateRanges]
                        newRanges[index].start = e.target.value
                        setDateRanges(newRanges)
                      }}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                    <input
                      type="date"
                      value={range.end}
                      onChange={(e) => {
                        const newRanges = [...dateRanges]
                        newRanges[index].end = e.target.value
                        setDateRanges(newRanges)
                      }}
                      min={range.start || new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                      required
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={() => {
                        const newRanges = dateRanges.filter((_, i) => i !== index)
                        setDateRanges(newRanges)
                      }}
                      className="w-full px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  setDateRanges([...dateRanges, { start: '', end: '' }])
                }}
                className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors text-sm font-medium"
              >
                + Add Date Range
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Add multiple date ranges to specify when your property is available for shortlet bookings.
            </p>
          </div>
        )}

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Description *
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
            placeholder="Describe your property, amenities, and any important details..."
            required
          />
        </div>

        {/* Amenities */}
        <div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Available Amenities
            </label>
            <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-gray-300 rounded-md p-3 bg-white">
              {amenitiesOptions.map(amenity => (
                <label key={amenity.value} className="flex items-center space-x-2 text-sm">
                  <input
                    type="checkbox"
                    checked={formData.amenities.includes(amenity.value)}
                    onChange={() => handleAmenityToggle(amenity.value)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">{amenity.label}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Select all amenities available in your property
            </p>
          </div>
        </div>

        {/* Image Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Property Images
          </label>
          
          {/* Property Image Manager */}
          <PropertyImageManager
            propertyId={property?.id || 'new'}
            initialImages={images}
            onImagesChange={handleImagesChange}
            maxImages={20}
            maxFileSize={10}
          />
        </div>

        {/* Video Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Property Videos
          </label>
          <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <h4 className="font-medium text-purple-900 mb-2">🎥 Video Guidelines</h4>
            <div className="text-sm text-purple-800 space-y-1 mb-3">
              <p>• <strong>File Types:</strong> MP4, MOV, AVI, WebM</p>
              <p>• <strong>Size Limit:</strong> Maximum 50MB per video</p>
              <p>• <strong>Duration:</strong> Maximum 5 minutes per video</p>
              <p>• <strong>Content:</strong> Show property walkthrough, amenities, neighborhood</p>
            </div>
            <p className="text-xs text-purple-700">
              Videos help clients better understand your property. Show different rooms, 
              amenities, and the surrounding area for maximum appeal.
            </p>
          </div>
          
          {/* Video Upload Button */}
          <div className="mb-4">
            <input
              ref={videoFileInputRef}
              type="file"
              accept="video/*"
              multiple
              onChange={handleVideoUpload}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => videoFileInputRef.current?.click()}
              disabled={uploadingVideos}
              className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploadingVideos ? 'Uploading...' : '🎥 Upload Videos'}
            </button>
            <p className="text-sm text-gray-500 mt-1">
              Upload up to 5 videos. First video will be the featured video.
            </p>
          </div>

          {/* Video Preview Grid */}
          {videos.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {videos.map((video, index) => (
                <div key={index} className="relative group">
                  <video
                    src={video}
                    className="w-full h-48 object-cover rounded-lg border"
                    controls
                    preload="metadata"
                  />
                  <button
                    type="button"
                    onClick={() => removeVideo(index)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    ×
                  </button>
                  {index === 0 && (
                    <div className="absolute top-2 left-2 bg-purple-500 text-white text-xs px-2 py-1 rounded">
                      Featured
                    </div>
                  )}
                  <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                    Video {index + 1}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* House Document Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            House Verification Documents *
          </label>
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">📋 Required for Property Verification</h4>
            <div className="text-sm text-blue-800 space-y-1 mb-3">
              <p>• <strong>Title Deed</strong> or Certificate of Occupancy (C of O)</p>
              <p>• <strong>Survey Plan</strong> or Site Plan</p>
              <p>• <strong>Building Approval</strong> or Development Permit</p>
              <p>• <strong>Tax Clearance</strong> or Receipt</p>
              <p>• <strong>Utility Bills</strong> (optional but recommended)</p>
            </div>
            <p className="text-xs text-blue-700">
              These documents help admin verify property ownership and authenticity. 
              Properties with complete documentation are verified faster.
            </p>
          </div>
          
          {/* House Document Upload Button */}
          <div className="mb-4">
            <input
              ref={docsFileInputRef}
    
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              multiple
              onChange={handleHouseDocumentUpload}
              className="hidden"
            />
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                docsFileInputRef.current?.click()
              }}
              disabled={uploadingDocs}
    
              className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {uploadingDocs ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Upload House Documents
                </>
              )}
            </button>
            <p className="text-sm text-gray-500 mt-1">
              Upload PDF or image files (max 10MB each). <span className="text-red-600 font-medium">At least one document is required.</span> Uploaded documents are not saved until you click the &quot;{property ? 'Update Property' : 'Create Property'}&quot; button below.
            </p>
          </div>

          {/* House Document Preview Grid */}
          {houseDocuments.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {houseDocuments.map((doc, index) => (
                <div key={index} className="relative group">
                  <div className="w-full h-32 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-4xl text-gray-400 mb-2">📄</div>
                      <div className="text-xs text-gray-500">Document {index + 1}</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeHouseDocument(index)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    ×
                  </button>
                  <a
                    href={doc}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute bottom-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    View
                  </a>
                </div>
              ))}
            </div>
          ) : (
            <div className="border-2 border-dashed border-red-300 rounded-lg p-6 text-center bg-red-50">
              <div className="text-red-400 text-4xl mb-2">📄</div>
              <p className="text-red-600 font-medium">No documents uploaded</p>
              <p className="text-red-500 text-sm">Please upload at least one verification document</p>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={loading || houseDocuments.length === 0}
            aria-label={property ? 'Save property changes' : 'Create property'}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : (property ? 'Update Property' : 'Create Property')}
          </button>
        </div>
      </form>
    </div>
  )
} 

